import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BFF_CONFIG, BFFConfig } from './bff-config.types';
import { BFF_REQUEST } from './bff-error.interceptor';
import { SKIP_403_HANDLING } from '@interceptors/bff-forbidden.interceptor';
import {
  BeheerderPostLeerkrachtModel,
  Deelnemer,
  DeelnemerDTO,
  DeelnemerInputModel,
  DeelnemerModel,
  DeelnemerPlanningModel,
  KenmerkColumnApiModel,
  Leerkracht,
  LeerkrachtInputModel,
  OnderwijsLocatie,
  PlanningApiModel,
  PlanningInputModelDTO,
  Schooladviezen,
  Settings,
  ProductKey,
  StartCodeByProduct,
  StartwoordStartcodeApiModel,
  ToetsApiModel,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class ApiClientService {
  constructor(
    private readonly http: HttpClient,
    @Inject(BFF_CONFIG) private readonly apiConfig: BFFConfig,
  ) {}

  get apiUrl() {
    return this.apiConfig.BFF.ApiUrl.replace(/\/$/, '');
  }

  putAntwoordbladNummers(deelnemerIds: string[]): Observable<unknown> {
    return this.http.put(`${this.apiUrl}/v1/deelnemers/antwoordbladnummers`, deelnemerIds, {
      context: new HttpContext().set(BFF_REQUEST, true),
    });
  }

  getOnderwijslocaties(): Observable<OnderwijsLocatie[]> {
    return this.http.get<OnderwijsLocatie[]>(`${this.apiUrl}/v1/onderwijslocaties`, {
      context: new HttpContext().set(BFF_REQUEST, true),
    });
  }

  updateOnderwijslocatieMetadata(
    onderwijslocatieId: string,
    body: { aanleveringDeelnemersLAS: boolean },
  ): Observable<{ adminNodeId: string }> {
    return this.http.post<{ adminNodeId: string }>(
      `${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}/metadata`,
      body,
      {
        context: new HttpContext().set(BFF_REQUEST, true),
      },
    );
  }

  deleteOnderwijslocatie(onderwijslocatieId: string): Observable<HttpResponse<unknown>> {
    return this.http.delete(`${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}`, {
      observe: 'response',
      context: new HttpContext().set(SKIP_403_HANDLING, true),
    });
  }

  deleteLeerkrachtFromOnderwijslocaties(onderwijslocatieId: string, persoonId: string): Observable<unknown> {
    return this.http.delete(`${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}/leerkrachten/${persoonId}`, {
      observe: 'response',
    });
  }

  getDeelnemers(onderwijslocatieId: string): Observable<DeelnemerModel[]> {
    return this.http.get<DeelnemerModel[]>(
      `${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}/deelnemers`,
      {
        context: new HttpContext().set(BFF_REQUEST, false),
      },
    );
  }

  getDeelnemersComplete(onderwijslocatieId: string): Observable<DeelnemerDTO[]> {
    return this.http.get<DeelnemerDTO[]>(
      `${this.apiUrl}/v1.1/onderwijslocaties/${onderwijslocatieId}/deelnemers?includePlanning=true&includeStartcode=true`,
      {
        context: new HttpContext().set(BFF_REQUEST, false),
      },
    );
  }

  createDeelnemer(onderwijslocatieId: string, body: DeelnemerInputModel): Observable<Deelnemer> {
    return this.http.post<Deelnemer>(`${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}/deelnemers`, body, {
      context: new HttpContext().set(BFF_REQUEST, true),
    });
  }

  readDeelnemer(onderwijslocatieId: string, persoonId: string): Observable<Deelnemer> {
    return this.http.get<Deelnemer>(
      `${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}/deelnemers/${persoonId}`,
      {
        context: new HttpContext().set(BFF_REQUEST, true),
      },
    );
  }

  updateDeelnemer(onderwijslocatieId: string, persoonId: string, body: DeelnemerInputModel): Observable<unknown> {
    return this.http.put(`${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}/deelnemers/${persoonId}`, body, {
      context: new HttpContext().set(BFF_REQUEST, true),
    });
  }

  deleteDeelnemer(onderwijslocatieId: string, persoonId: string): Observable<unknown> {
    return this.http.delete(`${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}/deelnemers/${persoonId}`, {
      observe: 'response',
    });
  }

  getKenmerkColumns(productBusinessKey: string): Observable<KenmerkColumnApiModel[]> {
    return this.http.get<KenmerkColumnApiModel[]>(`${this.apiUrl}/v1/producten/${productBusinessKey}/kenmerken`, {
      context: new HttpContext().set(BFF_REQUEST, false),
    });
  }

  getDeelnemerPlanningen(onderwijsLocatieId: string): Observable<DeelnemerPlanningModel[]> {
    return this.http.get<DeelnemerPlanningModel[]>(
      `${this.apiUrl}/v1/onderwijslocaties/${onderwijsLocatieId}/deelnemerplanningen`,
      {
        context: new HttpContext().set(BFF_REQUEST, true),
      },
    );
  }

  getToetsen(productBusinessKey: string): Observable<ToetsApiModel[]> {
    return this.http.get<ToetsApiModel[]>(`${this.apiUrl}/v1/producten/${productBusinessKey}/toetsen`, {
      context: new HttpContext().set(BFF_REQUEST, false),
    });
  }

  getSettings(): Observable<Settings> {
    return this.http.get<Settings>(`${this.apiUrl}/v1/settings`, {
      context: new HttpContext().set(BFF_REQUEST, false),
    });
  }

  updateSettings(settings: Settings): Observable<unknown> {
    return this.http.put(`${this.apiUrl}/v1/settings`, settings, {
      observe: 'response',
    });
  }

  updatePlanningen(
    planning: PlanningInputModelDTO[],
    onderwijslocatieId: string,
    isInhaalToets = false,
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/v1.1/planningen?onderwijslocatieId=${onderwijslocatieId}&inhaaltoetsPlanningen=${isInhaalToets}`,
      planning,
      {
        context: new HttpContext().set(BFF_REQUEST, true),
      },
    );
  }

  postPlanning(
    planningResult: PlanningApiModel[],
    onderwijslocatieId: string,
    isInhaalToets = false,
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/v1.1/planningen?onderwijslocatieId=${onderwijslocatieId}&inhaaltoetsPlanningen=${isInhaalToets}`,
      planningResult,
      {
        observe: 'response',
      },
    );
  }

  getSchooladviezen(): Observable<Schooladviezen[]> {
    return this.http.get<Schooladviezen[]>(`${this.apiUrl}/v1/schooladviezen`, {
      context: new HttpContext().set(BFF_REQUEST, true),
    });
  }

  putSchooladvies(onderwijsLocatieId: string, deelnemerId: string, schooladviesId: object): Observable<unknown> {
    return this.http.put(
      `${this.apiUrl}/v1/onderwijslocaties/${onderwijsLocatieId}/deelnemers/${deelnemerId}/schooladvies`,
      schooladviesId,
      {
        observe: 'response',
      },
    );
  }

  updateSchooladvies(onderwijsLocatieId: string, deelnemerId: string, body: object): Observable<unknown> {
    return this.http.put(
      `${this.apiUrl}/v1/onderwijslocaties/${onderwijsLocatieId}/deelnemers/${deelnemerId}/schooladvies`,
      body,
    );
  }

  getLeerkrachtenBeheerders(): Observable<Leerkracht[]> {
    return this.http.get<Leerkracht[]>(`${this.apiUrl}/v1/leerkrachten`, {
      context: new HttpContext().set(BFF_REQUEST, true),
    });
  }

  getLeerkrachten(onderwijslocatieId: string): Observable<HttpResponse<Leerkracht[]>> {
    const params = new HttpParams().set('onderwijslocatieId', onderwijslocatieId);
    return this.http.get<Leerkracht[]>(`${this.apiUrl}/v1/leerkrachten`, {
      params,
      observe: 'response',
    });
  }

  postLeerkrachtAanLocaties(beheerdersPostModel: BeheerderPostLeerkrachtModel): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/v1/leerkrachten`, beheerdersPostModel, {
      observe: 'response',
    });
  }

  createLeerkracht(body: LeerkrachtInputModel): Observable<Leerkracht> {
    return this.http.post<Leerkracht>(`${this.apiUrl}/v1/leerkrachten`, body);
  }

  updateLeerkracht(persoonId: string, body: LeerkrachtInputModel): Observable<Leerkracht> {
    return this.http.put<Leerkracht>(`${this.apiUrl}/v1/leerkrachten/${persoonId}`, body);
  }

  deleteLeerkracht(persoonId: string): Observable<Leerkracht> {
    return this.http.delete<Leerkracht>(`${this.apiUrl}/v1/leerkrachten/${persoonId}`);
  }

  getStartwoordStartcode(onderwijslocatieId: string): Observable<StartwoordStartcodeApiModel[]> {
    return this.http.get<StartwoordStartcodeApiModel[]>(
      `${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}/startcodes`,
      {
        context: new HttpContext().set(BFF_REQUEST, false),
      },
    );
  }

  getAssetsAudioUrlForCAL(): Observable<HttpResponse<string>> {
    return this.http.get(`${this.apiUrl}/v1/assets/audio`, {
      responseType: 'text',
      observe: 'response',
    });
  }

  getAssetsToetsboekUrlForCAL(onderwijsLocatieId: string): Observable<HttpResponse<string>> {
    const params = new HttpParams().set('onderwijslocatieId', onderwijsLocatieId);
    return this.http.get(`${this.apiUrl}/v1/assets/toetsboek`, {
      params,
      responseType: 'text',
      observe: 'response',
    });
  }

  createStartcodes(onderwijslocatieId: string, productKeys?: ProductKey[]): Observable<StartCodeByProduct> {
    let params = new HttpParams();
    productKeys?.forEach((pk) => {
      params = params.append('productKeys', pk);
    });
    return this.http.post<StartCodeByProduct>(
      `${this.apiUrl}/v1.1/onderwijslocaties/${onderwijslocatieId}/startcodes/generate`,
      {},
      { params },
    );
  }

  createStartcodesForDeelnemers(
    onderwijslocatieId: string,
    deelnemerIds: string[],
    productKeys?: ProductKey[],
  ): Observable<StartCodeByProduct> {
    let params = new HttpParams();
    productKeys?.forEach((pk) => {
      params = params.append('productKeys', pk);
    });
    return this.http.post<StartCodeByProduct>(
      `${this.apiUrl}/v1.1/onderwijslocaties/${onderwijslocatieId}/startcodes/deelnemers/generate`,
      deelnemerIds,
      { params },
    );
  }

  createStartcodesForDeelnemerIds(
    onderwijslocatieId: string,
    persoonIds: string[],
    productKeys?: ProductKey[],
  ): Observable<StartCodeByProduct> {
    let params = new HttpParams();
    productKeys?.forEach((pk) => {
      params = params.append('productKeys', pk);
    });
    return this.http.post<StartCodeByProduct>(
      `${this.apiUrl}/v1.1/onderwijslocaties/${onderwijslocatieId}/startcodes/deelnemers/generate`,
      persoonIds,
      { params },
    );
  }

  readStartcodes(onderwijslocatieId: string, productKeys?: ProductKey[]): Observable<StartCodeByProduct> {
    let params = new HttpParams();
    productKeys?.forEach((pk) => {
      params = params.append('productKeys', pk);
    });
    return this.http.get<StartCodeByProduct>(
      `${this.apiUrl}/v1.1/onderwijslocaties/${onderwijslocatieId}/startcodes`,
      {
        context: new HttpContext().set(BFF_REQUEST, false),
        params,
      },
    );
  }

  deleteStartcodes(onderwijslocatieId: string, startcodes: string[] = []): Observable<unknown> {
    let params = new HttpParams();

    startcodes.forEach((startcode) => {
      params = params.append('startcode', startcode);
    });

    return this.http.delete(`${this.apiUrl}/v1/onderwijslocaties/${onderwijslocatieId}/startcodes`, {
      params,
      observe: 'response',
    });
  }

  downloadAlleLeerlingRapporten(onderwijslocatieId: string): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    params = params.append('onderwijslocatieId', onderwijslocatieId);

    return this.http.get(`${this.apiUrl}/v1/rapporten/leerling/zip`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  downloadGeregistreerdeScholenVoorDuo(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/v1/export/geregistreerdeScholenVoorDuo`, {
      responseType: 'blob',
      observe: 'response',
      context: new HttpContext().set(BFF_REQUEST, true),
    });
  }

  rapportenAvailable(onderwijslocatieId: string, rapportType: string) {
    let params = new HttpParams();
    params = params.append('onderwijslocatieId', onderwijslocatieId);

    return this.http.get(`${this.apiUrl}/v1/rapporten/${rapportType}/available`, {
      params,
      responseType: 'json',
      observe: 'response',
    });
  }

  leerlingRapportage(onderwijslocatieId: string, persoonId: string): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    params = params.append('onderwijslocatieId', onderwijslocatieId);
    params = params.append('persoonId', persoonId);

    return this.http.get(`${this.apiUrl}/v1/rapporten/leerling`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  groepsRapportage(onderwijslocatieId: string, groepId: string): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    params = params.append('onderwijslocatieId', onderwijslocatieId);
    params = params.append('groepid', groepId);

    return this.http.get(`${this.apiUrl}/v1/rapporten/groep`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  schoolRapportage(onderwijslocatieId: string): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    params = params.append('onderwijslocatieId', onderwijslocatieId);

    return this.http.get(`${this.apiUrl}/v1/rapporten/school`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }
}
